import {useChillEffect} from "../../../../util/mounting-hooks.js";
import {cleanChartData} from "./_helpers.js";
import dayjs from "dayjs";
import {MIN, SEC} from "../../../../util/time-and-date.js";
import {CHART_PALETTE, getChartGradients} from "../../chart/_chart-palette.js";
import {isMobile, trimArrayEvenly} from "../../../../util/misc.js";
import {useSelector} from "react-redux";
import {getSpecificAsset} from "../../../../ducks/assets-duck.js";
import {useState} from "react";
import {getStorage, setStorage} from "../../../../api/_token.js";
import {useQuery} from "@tanstack/react-query";
import {getRatesLiveApi} from "../../../../api/statusApi.js";
import {ASSET_IDS} from "../../../../util/asset-ids.js";

const MAX_ELEMENTS = 900
const dataInitialState = {
    labels: [],
    datasets: [],
    latestValue: 0,
    hasData: false,
    isLive: false,
    trajectory: 'neutral',
}


export const useLiveChartData = (assetId, chartRef) => {

    const localStorageKey = `live-rates-duration-${assetId}`
    const [duration, setDuration] = useState(getStorage(localStorageKey, 24))

    const {data: history = [], isLoading} = useQuery({
        queryKey: [`rates-live-api-${assetId}-${duration}`],
        queryFn: () => getRatesLiveApi(assetId, duration),
        refetchInterval: 2.5 * SEC,
    })

    const {
        is_usd_asset,
    } = useSelector(state => getSpecificAsset(state, assetId))

    const [data, setData] = useState(dataInitialState)

    const isUsdAsset = is_usd_asset && assetId !== ASSET_IDS.DAI

    const changeDuration = duration => {
        setDuration(duration)
        // setData(dataInitialState)
        setStorage(localStorageKey, duration)
    }

    useChillEffect(() => {

        const historyCleaned = trimArrayEvenly(cleanChartData(history, isUsdAsset).reverse(), MAX_ELEMENTS)

        const values = historyCleaned.map(i => isUsdAsset ? i.u : i.r)
        const labels = historyCleaned.map(i => i.d)

        const latestValue = values[0]
        const latestLabel = labels.filter(label => dayjs(label).second() > 5 || dayjs(label).minute() > 0)[0]
        const hasData = values.length > 1

        const timeDiff = dayjs().diff(latestLabel)
        const isLive = hasData && timeDiff < 15 * MIN && latestLabel

        let trajectory = 'neutral'
        const isPositive = values[values.length - 1] < latestValue
        const isNegative = values[values.length - 1] > latestValue
        if (isPositive) {
            trajectory = 'positive'
        } else if (isNegative) {
            trajectory = 'negative'
        }

        const maxValue = Math.max(...values)
        const minValue = Math.min(...values)
        const hasMovement = maxValue !== minValue

        const {
            lineGradient,
            backgroundGradient,
        } = getChartGradients(isPositive ? CHART_PALETTE.POSITIVE : CHART_PALETTE.NEGATIVE, chartRef)

        setData({
            trajectory,
            latestValue,
            hasData,
            hasMovement,
            isLive,
            labels,
            isNegative,
            firstValue: values[values.length - 1],
            firstLabel: labels[labels.length - 1],
            maxValue,
            minValue,
            datasets: [{
                label: 'Values',
                data: values,
                pointRadius: 0,
                pointHoverRadius: 8,
                borderWidth: isMobile() ? 2.2 : 3.5,
                borderColor: lineGradient,
                backgroundColor: backgroundGradient,
                pointHoverBackgroundColor: backgroundGradient,
                lineTension: 0.15,
                fill: true,
            }],
        })
    }, [history.toString()])

    return {
        isUsdAsset,
        isLoading,
        duration,
        changeDuration,
        ...data,
    }
}